/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';

/*
 * Server-rendered timestamps are UTC; the reader wants their own zone.
 *
 * Elements carry the UTC value as their text and the wanted format in
 * `data-time-format`, and are marked `.time-format` until converted.
 */

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

let localeApplied = false;

function applyLocale() {
    if (localeApplied) return;
    dayjs.locale(
        document.documentElement.lang || window.navigator.language || 'en'
    );
    localeApplied = true;
}

/**
 * Convert every unconverted timestamp in `root` to the reader's zone.
 *
 * Call it again after inserting content. It used to re-run itself on
 * jQuery's global `ajaxComplete`, which only ever saw jQuery's own
 * requests and has no native equivalent — the caller that inserted the
 * content is the one that knows it happened.
 *
 * @param {ParentNode} [root] - Where to look. Defaults to the document.
 * @returns {number} How many elements were converted.
 */
export function formatTimes(root = document) {
    applyLocale();
    let converted = 0;
    root.querySelectorAll('.time-format').forEach((element) => {
        const format = element.dataset.timeFormat;
        // `textContent`, not `innerText`: the value is a machine timestamp,
        // so layout has no say in it, and `innerText` reads as empty on an
        // element that is currently hidden.
        const local = dayjs.utc(element.textContent).local();
        const time = local.format(format);
        if (time !== 'Invalid Date') {
            element.textContent = time;
            converted += 1;
        } else {
            console.warn(`Invalid time for Day.js: ${element.textContent}`);
        }
        element.classList.remove('time-format');
    });
    return converted;
}

export default formatTimes;
